import {inject} from '@angular/core';
import {
  catchError,
  concatMap,
  finalize,
  map,
  mergeMap,
  of, switchMap,
  withLatestFrom
} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from "@ngrx/store";
import {VisitConfigurationAPiActions} from "./visit-configuration.action";
import {CommonsFacadeService} from "../../../../commons/angular/state/commons/commons-facade.service";
import {VisitConfigurationHttpService} from "../../services/visit-configuration-http.service";
import {VisitConfigurationModel} from "../../../models/visit-configuration.model";
import {visitConfigurationsFeature} from "./visit-configuration.state";
import {ToastService} from "../../../../commons/angular/services/toast.service";

export const getInfiniteScroll = createEffect(
  (actions$ = inject(Actions),
   visitConfigurationHttpService = inject(VisitConfigurationHttpService),
   visitConfigurationStore = inject(Store<VisitConfigurationModel>),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitConfigurationAPiActions.getInfiniteScroll),
      concatMap(payload =>
        of(payload).pipe(
          withLatestFrom(visitConfigurationStore.select(visitConfigurationsFeature.selectCursor()))
        )
      ),
      mergeMap(([payload, cursor]) => {
        commonsFacadeService.setLoaderOn();
        let paramsCopy = {...payload.params}
        if (!!cursor) {
          paramsCopy.cursor = cursor;
        }
        return visitConfigurationHttpService.getMany(paramsCopy).pipe(
          map((res: {
            cursor: string,
            list: VisitConfigurationModel[]
          }) => VisitConfigurationAPiActions.getInfiniteScrollSuccess({
            visitConfigurations: res.list,
            cursor: res.cursor
          })),
          catchError((error: {
            message: string
          }) => of(VisitConfigurationAPiActions.getInfiniteScrollFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getOne = createEffect(
  (actions$ = inject(Actions),
   visitConfigurationHttpService = inject(VisitConfigurationHttpService),
   commonsFacadeService = inject(CommonsFacadeService),
  ) => {
    return actions$.pipe(
      ofType(VisitConfigurationAPiActions.getOne),
      mergeMap(payload => {
        return visitConfigurationHttpService.getOne(payload.id).pipe(
          map((visitConfiguration: VisitConfigurationModel) => VisitConfigurationAPiActions.getOneSuccess({
            visitConfiguration: visitConfiguration,
            isSubscribeToChannel: payload.isSubscribeToChannel
          })),
          catchError((error: {
            message: string
          }) => of(VisitConfigurationAPiActions.getOneFailure({error})))
        )
      })
    );
  },
  {functional: true}
);

export const createOne = createEffect(
  (actions$ = inject(Actions),
   visitConfigurationHttpService = inject(VisitConfigurationHttpService),
   visitConfigurationsStore = inject(Store<VisitConfigurationModel>),
   commonsFacadeService = inject(CommonsFacadeService),
   toastService = inject(ToastService)
  ) => {
    return actions$.pipe(
      ofType(VisitConfigurationAPiActions.createOne),
      switchMap(payload => {
        commonsFacadeService.setLoaderOn();
        return visitConfigurationHttpService.createOne(payload.visitConfiguration).pipe(
          map((createdVisitConfiguration) => {
            toastService.success('VISIT_CONFIGURATION_CREATE_SUCCESS');
            return VisitConfigurationAPiActions.createOneSuccess({visitConfiguration: createdVisitConfiguration})
          }),
          catchError((error: { message: string }) => {
            toastService.error('VISIT_CONFIGURATION_CREATE_ERROR');
            return of(VisitConfigurationAPiActions.createOneFailure({error}))
          }),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const updateOne = createEffect(
  (actions$ = inject(Actions),
   visitConfigurationHttpService = inject(VisitConfigurationHttpService),
   visitConfigurationsStore = inject(Store<VisitConfigurationModel>),
   commonsFacadeService = inject(CommonsFacadeService),
   toastService = inject(ToastService),
  ) => {
    return actions$.pipe(
      ofType(VisitConfigurationAPiActions.updateOne),
      switchMap(payload => {
        commonsFacadeService.setLoaderOn();
        return visitConfigurationHttpService.updateOne(payload.visitConfiguration.id, payload.visitConfiguration).pipe(
          map((createdVisitConfiguration) => {
            toastService.success('VISIT_CONFIGURATION_UPDATE_SUCCESS');
            return VisitConfigurationAPiActions.updateOneSuccess({visitConfiguration: createdVisitConfiguration})
          }),
          catchError((error: { message: string }) => {
            toastService.error('VISIT_CONFIGURATION_UPDATE_ERROR');
            return of(VisitConfigurationAPiActions.updateOneFailure({error}))
          }),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const deleteOne = createEffect(
  (actions$ = inject(Actions),
   visitConfigurationHttpService = inject(VisitConfigurationHttpService),
   visitConfigurationsStore = inject(Store<VisitConfigurationModel>),
   commonsFacadeService = inject(CommonsFacadeService),
   toastService = inject(ToastService),
  ) => {
    return actions$.pipe(
      ofType(VisitConfigurationAPiActions.deleteOne),
      switchMap(payload => {
        commonsFacadeService.setLoaderOn();
        return visitConfigurationHttpService.deleteOne(payload.id).pipe(
          map((createdVisitConfiguration) => {
            toastService.success('VISIT_CONFIGURATION_DELETE_SUCCESS');
            return VisitConfigurationAPiActions.deleteOneSuccess({id: payload.id})
          }),
          catchError((error: { message: string }) => {
            toastService.error('VISIT_CONFIGURATION_DELETE_ERROR');
            return of(VisitConfigurationAPiActions.deleteOneFailure({error}))
          }),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getMany = createEffect(
  (actions$ = inject(Actions),
   visitConfigurationHttpService = inject(VisitConfigurationHttpService),
   visitConfigurationsStore = inject(Store<VisitConfigurationModel>),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitConfigurationAPiActions.getMany),
      concatMap(payload =>
        of(payload).pipe(
          withLatestFrom(visitConfigurationsStore.select(visitConfigurationsFeature.selectCursor()))
        )
      ),
      mergeMap(([payload, cursor]) => {
        commonsFacadeService.setLoaderOn();
        let paramsCopy = {...payload.params}
        if (!!cursor) {
          paramsCopy.cursor = cursor;
        }
        return visitConfigurationHttpService.getMany(paramsCopy).pipe(
          map((res: {
            cursor: string,
            list: VisitConfigurationModel[]
          }) => VisitConfigurationAPiActions.getManySuccess({visitConfigurations: res.list, cursor: res.cursor})),
          catchError((error: { message: string }) => of(VisitConfigurationAPiActions.getManyFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

// export const getMany = createEffect(
//   (actions$ = inject(Actions),
//    visitConfigurationHttpService = inject(VisitConfigurationHttpService),
//    commonsFacadeService = inject(CommonsFacadeService)
//   ) => {
//     return actions$.pipe(
//       ofType(VisitConfigurationAPiActions.getMany),
//       mergeMap((payload) => {
//         commonsFacadeService.setLoaderOn();
//         return visitConfigurationHttpService.getMany(payload.params).pipe(
//           map((res: {
//             list: VisitConfigurationModel[]
//             cursor: string
//           }) => VisitConfigurationAPiActions.getManySuccess({visitConfigurations: res.list, cursor: res.cursor})),
//           catchError((error: {
//             message: string
//           }) => of(VisitConfigurationAPiActions.getManyFailure({error}))),
//           finalize(() => {
//             commonsFacadeService.setLoaderOff()
//           })
//         )
//       })
//     );
//   },
//   {functional: true}
// );

// export const getOneSuccess = createEffect(
//   (actions$ = inject(Actions),
//    // servicesChannelService = inject(ServicesChanelService),
//    visitConfigurationStore = inject(Store<VisitConfigurationModel>)) => {
//     return actions$.pipe(
//       ofType(VisitConfigurationAPiActions.getOneSuccess),
//       map((payload) => {
//         if (payload.isSubscribeToChannel) {
//           // servicesChannelService.subscribeById(payload.service.id);
//         }
//       })
//     );
//   },
//   {functional: true, dispatch: false}
// );
//
// export const getServicesSuccess = createEffect(
//   (actions$ = inject(Actions),
//    // servicesChannelService = inject(ServicesChanelService),
//    visitConfigurationStore = inject(Store<VisitConfigurationModel>)) => {
//     return actions$.pipe(
//       ofType(VisitConfigurationAPiActions.getManySuccess),
//       map((payload) => {
//         if (payload.isSubscribeToChannel) {
//           // const serviceIds = payload.services.map(l => l.id);
//           // serviceIds.forEach(serviceId => servicesChannelService.subscribeById(serviceId));
//         }
//       })
//     );
//   },
//   {functional: true, dispatch: false}
// );
//
//
// export const getInfiniteScrollSuccess = createEffect(
//   (actions$ = inject(Actions),
//    // servicesChannelService = inject(ServicesChanelService),
//    visitConfigurationStore = inject(Store<VisitConfigurationModel>)) => {
//     return actions$.pipe(
//       ofType(VisitConfigurationAPiActions.getInfiniteScrollSuccess),
//       map((payload) => {
//         if (payload.isSubscribeToChannel) {
//           // const serviceIds = payload.services.map(l => l.id);
//           // serviceIds.forEach(serviceId => servicesChannelService.subscribeById(serviceId));
//         }
//       })
//     );
//   },
//   {functional: true, dispatch: false}
// );

// export const subscribeSetServicesChannels = createEffect(
//   (actions$ = inject(Actions),
//    servicesChannelService = inject(ServicesChanelService),
//    servicesStore = inject(Store<Service>)) => {
//     return actions$.pipe(
//       ofType(ServicesActions.setServices),
//       map((payload) => {
//         const serviceIds = payload.services.map(l => l.id);
//         serviceIds.forEach(serviceId => servicesChannelService.subscribeById(serviceId));
//       })
//     );
//   },
//   {functional: true, dispatch: false}
// );

// export const subscribeAllServicesChannel = createEffect(
//   (actions$ = inject(Actions),
//    servicesChanelService = inject(ServicesChanelService)) => {
//     return actions$.pipe(
//       ofType(VisitConfigurationChannelActions.subscribe),
//       mergeMap(payload =>
//         servicesChanelService.getChannelsValues().pipe(
//           map((notification) => {
//             return VisitConfigurationChannelActions.notificationReceived({notification: notification})
//           })
//         )
//       )
//     );
//   },
//   {functional: true}
// );
//
// export const subscribeAllPartialServicesChannel = createEffect(
//   (actions$ = inject(Actions),
//    servicesChanelService = inject(ServicesChanelService)) => {
//     return actions$.pipe(
//       ofType(VisitConfigurationChannelActions.subscribePartial),
//       mergeMap(payload =>
//         servicesChanelService.getPartialChannelsValues().pipe(
//           map((notification) => {
//             return VisitConfigurationChannelActions.partialNotificationReceived({notification: notification})
//           })
//         )
//       )
//     );
//   },
//   {functional: true}
// );
//
// export const unsubscribeAllServiceChannels = createEffect(
//   (actions$ = inject(Actions),
//    servicesChannelService = inject(ServicesChanelService)) => {
//     return actions$.pipe(
//       ofType(VisitConfigurationChannelActions.unsubscribe, ServicesActions.removeAll, ServicesActions.clearStore),
//       map(payload => {
//           servicesChannelService.unSubscribeAll();
//           return VisitConfigurationChannelActions.unsubscribeSuccess();
//         }
//       )
//     );
//   },
//   {functional: true}
// );
//
//
// export const unsubscribeServiceChannels = createEffect(
//   (actions$ = inject(Actions),
//    servicesChannelService = inject(ServicesChanelService)) => {
//     return actions$.pipe(
//       ofType(ServicesActions.removeServices),
//       map(payload => {
//           payload.ids?.forEach(serviceId => servicesChannelService.unSubscribeById(serviceId));
//           return VisitConfigurationChannelActions.unsubscribeSuccess();
//         }
//       )
//     );
//   },
//   {functional: true}
// );





